package app.bloomey.web.core.offCanvas.items

import androidx.compose.runtime.*
import app.bloomey.web.components.modals.Toast
import app.bloomey.web.components.widgets.*
import app.bloomey.web.core.data.models.UpdateVideoRequest
import app.bloomey.web.core.data.models.Video
import app.bloomey.web.core.data.models.VideoProvider
import app.bloomey.web.core.modal.LocalSiteModelController
import app.bloomey.web.core.modal.modals.DeleteItemModal
import app.bloomey.web.core.offCanvas.LocalOffCanvasController
import app.bloomey.web.core.offCanvas.OffCanvasItem
import app.bloomey.web.core.res.Res
import app.bloomey.web.core.res.icClose
import app.bloomey.web.core.res.icTrash
import app.bloomey.web.modules.video.VideoContract
import app.bloomey.web.modules.video.VideoViewModel
import app.bloomey.web.theme.SpanTextHeader1Variant
import app.bloomey.web.theme.toSitePalette
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.compose.util.kebabCaseToTitleCamelCase
import com.varabyte.kobweb.silk.components.forms.InputStyle
import com.varabyte.kobweb.silk.components.forms.OutlinedInputVariant
import com.varabyte.kobweb.silk.components.style.toModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.coroutines.flow.collectLatest
import kotlinx.datetime.Instant
import kotlinx.datetime.toJSDate
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text
import kotlin.time.Duration.Companion.seconds

data class VideoOffCanvasItem(val id: String, val video: Video? = null, val onModified: () -> Unit) : OffCanvasItem {
    override val pathId: String
        get() = "video:$id"

    @Composable
    override fun Content(modifier: Modifier) {
        val sitePalette = ColorMode.current.toSitePalette()
        val offCanvasController = LocalOffCanvasController.current
        val siteModalController = LocalSiteModelController.current
        var title by remember { mutableStateOf(video?.title ?: "") }
        var imageUrl by remember { mutableStateOf(video?.imageUrl ?: "") }
        var categoryId by remember { mutableStateOf(video?.categoryId ?: "") }
        var videoId by remember { mutableStateOf(video?.videoId ?: "") }
        var duration by remember { mutableStateOf(video?.duration ?: 0.seconds) }
        var provider by remember { mutableStateOf(video?.provider ?: VideoProvider.youtube) }

        val scope = rememberCoroutineScope()
        val viewModel = remember { VideoViewModel(scope) }
        val uiState by viewModel.observeStates().collectAsState()

        LaunchedEffect(Unit) {
            viewModel.observeStates().collectLatest {
                if (it.updateVideo.isSuccess || it.deleteVideo.isSuccess) {
                    offCanvasController.pop(this@VideoOffCanvasItem)
                    onModified()
                }
            }
        }

        Column(
            modifier = modifier
                .padding(40.px)
                .background(sitePalette.background)
        ) {
            Row(modifier = Modifier.fillMaxWidth()) {
                SvgLoader(
                    Res.Assets.icClose,
                    modifier = Modifier.size(24.px)
                        .cursor(Cursor.Pointer)
                        .onClick {
                            offCanvasController.pop()
                        },
                    color = sitePalette.onBackgroundLight
                )
                Spacer()
                if (uiState.deleteVideo.isLoading)
                    LoadingView(modifier = Modifier.maxSize(24.px), size = 24.px)
                else
                    SvgLoader(
                        Res.Assets.icTrash,
                        modifier = Modifier.size(24.px)
                            .cursor(Cursor.Pointer)
                            .onClick {
                                siteModalController.push(
                                    DeleteItemModal(
                                        title = "Delete Video",
                                        text = "Are you sure you want to delet this video?",
                                        onDelete = {
                                            viewModel.trySend(VideoContract.Inputs.DeleteVideo(id))
                                        }
                                    )
                                )
                            },
                        color = Colors.Red
                    )
            }
            Height(20.px)
            SpanText("Video", variant = SpanTextHeader1Variant)
            Height(4.px)
            SpanText("#34223122")
            Height(4.px)
            video?.let {
                SpanText("UserId: #${video.id}", modifier = Modifier.cursor(Cursor.Pointer)
                    .onClick {
                        offCanvasController.push(UserOffCanvas(video.userId))
                    })
            }
            Height(40.px)
            val providerOptions = VideoProvider.entries.toList()
            Column(modifier = Modifier.fillMaxWidth().gap(20.px)) {
                SiteTextInput(label = "Title", text = title, modifier = Modifier.fillMaxWidth()) {
                    title = it
                }
                SiteTextInput(label = "Image Url", text = imageUrl, modifier = Modifier.fillMaxWidth()) {
                    imageUrl = it
                }
                SiteTextInput(label = "Category Id", text = categoryId, modifier = Modifier.fillMaxWidth()) {
                    categoryId = it
                }
                SiteTextInput(label = "Video Id", text = videoId, modifier = Modifier.fillMaxWidth()) {
                    videoId = it
                }
                SiteTextInput(
                    label = "Duration",
                    text = duration.toString(),
                    modifier = Modifier.fillMaxWidth(),
                    enabled = false
                ) {
                    it.toLongOrNull()?.let {

                    }
                }
                Select(attrs = InputStyle
                    .toModifier(OutlinedInputVariant)
                    .padding(leftRight = 0.625.cssRem)
                    .fontSize(1.cssRem)
                    .borderRadius(0.375.cssRem)
                    .fillMaxWidth()
                    .height(58.px).toAttrs {}) {
                    DisposableEffect(this) {
                        scopeElement.selectedIndex = providerOptions.indexOf(provider)
                        scopeElement.onchange = {
                            providerOptions.elementAt(scopeElement.selectedIndex).let {
                                provider = it
                            }
                        }
                        onDispose() {

                        }
                    }
                    providerOptions.forEach {
                        Option(value = it.name) {
                            Text(it.name.kebabCaseToTitleCamelCase())
                        }
                    }
                }
                video?.let {
                    Row {
                        SpanText("Created At: ")
                        Instant.fromEpochMilliseconds(it.createdAt).toJSDate().toDateString().also {
                            SpanText(it)
                        }
                    }
                }
            }
            Height(30.px)
            Spacer()

            SiteButton(
                loading = uiState.updateVideo.isLoading,
                modifier = Modifier.fillMaxWidth(),
                onClick = {
                    viewModel.trySend(
                        VideoContract.Inputs.UpdateVideo(
                            id, UpdateVideoRequest(
                                videoId = videoId,
                                title = title,
                                imageUrl = imageUrl,
                                categoryId = categoryId,
                                duration = duration,
                                provider = provider
                            )
                        )
                    )
                }) {
                SpanText("Update")
            }
        }


        Toast(message = uiState.updateVideo.getMessage.toString(), show = uiState.updateVideo.isError) {

        }
        Toast(message = uiState.deleteVideo.getMessage.toString(), show = uiState.deleteVideo.isError) {

        }
    }
}